import type { CustomCellRendererProps } from 'ag-grid-react'
import { Store, GitFork, ImageIcon, Loader2, MessageCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { ItemActionsMenu } from './ItemActionsMenu'
import { SkuSearchCell, type SkuSaveData } from './SkuSearchCell'
import { EditableCell } from './EditableCell'
import type { PlanDetailItem } from '@/types'
import type { DagRowRead } from '@/types/dag'
import type { RowData } from './useProductsTableState'
import { rowDataToPlanDetailItem } from './useProductsTableState'
import { useDagTableStore } from '@/stores/useDagTableStore'
import { useDagCellKeyboard } from './useDagCellKeyboard'

// Option item type for expanded rows
export interface OptionItem {
  id: string
  name: string
  imageUrl?: string
  colorName?: string
}

// NameCell - Combined cell with checkbox, name, and variant count
interface NameCellProps extends CustomCellRendererProps<RowData> {
  isSelected: boolean
  onToggleSelect: (checked: boolean) => void
  onSaveEdit: (itemId: string, field: string, value: string) => void
  navigateTab?: (
    rowId: string,
    colId: string,
    direction: 'forward' | 'backward'
  ) => boolean | void
  navigateEnter?: (
    rowId: string,
    colId: string,
    direction: 'down' | 'up'
  ) => void
  isReadOnly?: boolean
  onOpenCarousel?: (lineId: string) => void
}

export function NameCell(props: NameCellProps) {
  const storeEditing = useDagTableStore(s => s.editingCell)
  const clearEditing = useDagTableStore(s => s.clearEditing)
  const setEditingCell = useDagTableStore(s => s.setEditingCell)

  const row = props.data
  const isDescriptionEditable =
    !props.isReadOnly && (!row?.cells?.plan_sku_num || !!row?.cells?.is_new_sku)
  const isEditing =
    isDescriptionEditable &&
    storeEditing?.rowId === row?.id &&
    storeEditing?.colId === 'description'
  const { focusRef, isFocused, handleCellClick, handleDisplayKeyDown } =
    useDagCellKeyboard({
      rowId: row?.id,
      colId: 'description',
      isEditing,
      isEditable: isDescriptionEditable,
      navigateTab: props.navigateTab,
      navigateEnter: props.navigateEnter,
    })
  if (!row) return null

  const {
    isSelected,
    onToggleSelect,
    onSaveEdit,
    navigateTab,
    navigateEnter,
    onOpenCarousel,
  } = props

  const description = String(row.cells?.['description'] ?? '')
  const thumbnailUrl = row.images?.default_thumbnail_url ?? null
  const isGeneratingThumbnail = row.images?.thumbnail_status === 'PENDING'

  const handleCancelEdit = () => {
    // Only clear the store if it still points at this cell — a stale blur
    // (fired after keyboard navigation already moved editing elsewhere)
    // must not wipe out a different cell's active edit.
    const current = useDagTableStore.getState().editingCell
    if (current?.rowId === row.id && current?.colId === 'description') {
      clearEditing()
    }
  }

  return (
    <div className="flex items-center gap-2.5 w-full h-full px-4">
      {/* Checkbox */}
      <Checkbox
        checked={isSelected}
        onCheckedChange={checked => onToggleSelect(checked === true)}
        className="cursor-pointer"
      />

      {/* Thumbnail */}
      {isGeneratingThumbnail ? (
        <div className="w-[43px] h-[48px] bg-[#f8f8f8] rounded flex items-center justify-center shrink-0">
          <Loader2 className="size-5 animate-spin text-muted-foreground" />
        </div>
      ) : thumbnailUrl ? (
        <button
          onClick={() => onOpenCarousel?.(row.id)}
          className="shrink-0 cursor-pointer"
        >
          <img
            src={thumbnailUrl}
            alt="Item thumbnail"
            loading="lazy"
            className="w-[43px] h-[48px] object-cover rounded"
          />
        </button>
      ) : (
        <div className="w-[43px] h-[48px] bg-[#f8f8f8] rounded flex items-center justify-center shrink-0">
          <ImageIcon className="size-6 text-muted-foreground" />
        </div>
      )}

      {/* Name and variant count */}
      <div
        ref={focusRef}
        tabIndex={isFocused && !isEditing ? 0 : -1}
        onClick={handleCellClick}
        onKeyDown={!isEditing ? handleDisplayKeyDown : undefined}
        className={`flex flex-col justify-center min-w-0 flex-1 ${isFocused && !isEditing ? 'dag-cell-focused' : ''}`}
      >
        <EditableCell
          value={description}
          onSave={value => {
            handleCancelEdit()
            onSaveEdit(row.id, 'description', value)
          }}
          isEditing={isEditing}
          onStartEdit={
            isDescriptionEditable
              ? () => setEditingCell(row.id, 'description')
              : () => {}
          }
          onCancelEdit={handleCancelEdit}
          onTab={shiftKey =>
            navigateTab?.(
              row.id,
              'description',
              shiftKey ? 'backward' : 'forward'
            )
          }
          onEnter={shiftKey =>
            navigateEnter?.(row.id, 'description', shiftKey ? 'up' : 'down')
          }
        />
      </div>
    </div>
  )
}

// OptionRowCell - For expanded child items (options/variants)
interface OptionRowCellProps {
  option: OptionItem
}

export function OptionRowCell({ option }: OptionRowCellProps) {
  return (
    <div className="flex items-center gap-3 w-full h-full pl-[76px] pr-4">
      {/* Image thumbnail */}
      {option.imageUrl ? (
        <img
          src={option.imageUrl}
          alt={option.name}
          loading="lazy"
          className="w-[43px] h-[48px] object-cover rounded"
        />
      ) : (
        <div className="w-[43px] h-[48px] bg-gray-100 rounded flex items-center justify-center">
          <span className="text-xs text-muted-foreground">No img</span>
        </div>
      )}

      {/* Name and color */}
      <div className="flex flex-col justify-center min-w-0 flex-1">
        <span className="font-semibold text-sm text-[#1c1c1c]">
          {option.name}
        </span>
        {option.colorName && (
          <span className="text-xs text-muted-foreground">
            Color: {option.colorName}
          </span>
        )}
      </div>
    </div>
  )
}

// SKU Cell - Wraps existing SkuSearchCell component
interface SkuCellProps extends CustomCellRendererProps<RowData> {
  onSkuSave: (itemId: string, data: SkuSaveData, rowVersion: number) => void
  isReadOnly?: boolean
}

type SkuCellContext = {
  navigateTab?: (
    rowId: string,
    colId: string,
    direction: 'forward' | 'backward'
  ) => boolean | void
  navigateEnter?: (
    rowId: string,
    colId: string,
    direction: 'down' | 'up'
  ) => void
  getBulkSelectedRows?: (rowId: string) => DagRowRead[] | null
  onBulkCellEdit?: (
    selectedRows: DagRowRead[],
    columnId: string,
    value: unknown
  ) => void
}

export function SkuCell(props: SkuCellProps) {
  const storeEditing = useDagTableStore(s => s.editingCell)
  const clearEditing = useDagTableStore(s => s.clearEditing)
  const setEditingCell = useDagTableStore(s => s.setEditingCell)

  const row = props.data
  const context = props.context as SkuCellContext | undefined
  const isEditing =
    !props.isReadOnly &&
    storeEditing?.rowId === row?.id &&
    storeEditing?.colId === 'plan_sku_num'
  const { focusRef, isFocused, handleCellClick, handleDisplayKeyDown } =
    useDagCellKeyboard({
      rowId: row?.id,
      colId: 'plan_sku_num',
      isEditing,
      isEditable: !props.isReadOnly,
      navigateTab: context?.navigateTab,
      navigateEnter: context?.navigateEnter,
    })
  if (!row) return null

  const { onSkuSave, isReadOnly } = props
  const skuNumber = String(row.cells?.['plan_sku_num'] ?? '')
  const isNewSku = Boolean(row.cells?.['is_new_sku'])

  const handleCancelEdit = () => {
    // Only clear the store if it still points at this cell — a stale blur
    // (fired after keyboard navigation already moved editing elsewhere)
    // must not wipe out a different cell's active edit.
    const current = useDagTableStore.getState().editingCell
    if (current?.rowId === row.id && current?.colId === 'plan_sku_num') {
      clearEditing()
    }
  }

  return (
    <div
      ref={focusRef}
      tabIndex={isFocused && !isEditing ? 0 : -1}
      onClick={handleCellClick}
      onKeyDown={!isEditing ? handleDisplayKeyDown : undefined}
      className={`w-full h-full flex items-center gap-2 cursor-pointer ${isFocused && !isEditing ? 'dag-cell-focused' : ''}`}
      onDoubleClick={() =>
        !isReadOnly && !isEditing && setEditingCell(row.id, 'plan_sku_num')
      }
    >
      <SkuSearchCell
        value={skuNumber}
        isNewSku={isNewSku}
        onSave={data => {
          const selectedRows = context?.getBulkSelectedRows?.(row.id)
          if (selectedRows && context?.onBulkCellEdit) {
            context.onBulkCellEdit(selectedRows, 'plan_sku_num', data.skuNumber)
          } else {
            onSkuSave(row.id, data, row.row_version ?? 0)
          }
          clearEditing()
        }}
        isEditing={isEditing}
        onCancelEdit={handleCancelEdit}
        onTab={shiftKey => {
          return context?.navigateTab?.(
            row.id,
            'plan_sku_num',
            shiftKey ? 'backward' : 'forward'
          )
        }}
        onEnter={shiftKey => {
          context?.navigateEnter?.(
            row.id,
            'plan_sku_num',
            shiftKey ? 'up' : 'down'
          )
        }}
      />
    </div>
  )
}

// Line Actions Cell - Action buttons (store, fork, more menu)
interface LineActionsCellProps extends CustomCellRendererProps<RowData> {
  onAssignStoreGroup: (itemId: string) => void
  onMultiloc: (item: PlanDetailItem) => void
  onViewSecondaryAttributes: (itemId: string) => void
  onPlanWaves: (itemId: string) => void
  onCopyLine: (itemId: string) => void
  onDeleteLine: (itemId: string) => void
  triggerAutoSave: () => void
  isReadOnly?: boolean
  onAssignParentSku?: (itemId: string) => void
  onManagePIAB?: (itemId: string) => void
  onCreateForecast?: (itemId: string) => void
  onManageImages?: (itemId: string) => void
  onOpenComments?: (lineRefId: string) => void
}

export function LineActionsCell(props: LineActionsCellProps) {
  const row = props.data
  if (!row || row.isOptionRow) return null

  const {
    onAssignStoreGroup,
    onMultiloc,
    onViewSecondaryAttributes,
    onPlanWaves,
    onCopyLine,
    onDeleteLine,
    triggerAutoSave,
    isReadOnly,
    onAssignParentSku,
    onManagePIAB,
    onCreateForecast,
    onManageImages,
    onOpenComments,
  } = props

  const item = rowDataToPlanDetailItem(row)
  const skuLevel = (row.cells?.['sku_level'] as string) ?? null
  const lineRefId = row.line_ref_id ?? null
  const commentCount = row.comment_count ?? 0

  return (
    <TooltipProvider>
      <div className="flex items-center justify-center gap-1">
        {/* Assign Store Model */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => onAssignStoreGroup(item.id)}
              disabled={isReadOnly}
            >
              <Store className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Assign store model</p>
          </TooltipContent>
        </Tooltip>

        {/* Multiloc */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => onMultiloc(item)}
              disabled={isReadOnly}
            >
              <GitFork className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Multiloc</p>
          </TooltipContent>
        </Tooltip>

        {/* Comments */}
        {onOpenComments && lineRefId && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 relative"
                onClick={() => onOpenComments(lineRefId)}
                aria-label="Comments"
              >
                <MessageCircle className="h-4 w-4" />
                {commentCount > 0 && (
                  <Badge
                    variant="secondary"
                    className="absolute -right-1 -top-1 h-4 min-w-4 px-1 py-0 text-[10px] leading-none justify-center"
                  >
                    {commentCount}
                  </Badge>
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>
                {commentCount > 0
                  ? `${commentCount} comment${commentCount === 1 ? '' : 's'}`
                  : 'Comments'}
              </p>
            </TooltipContent>
          </Tooltip>
        )}

        {/* More Actions Menu */}
        <ItemActionsMenu
          itemId={item.id}
          onViewSecondaryAttributes={onViewSecondaryAttributes}
          onPlanWaves={onPlanWaves}
          onCopy={onCopyLine}
          onDelete={onDeleteLine}
          triggerAutoSave={triggerAutoSave}
          isReadOnly={isReadOnly}
          skuLevel={skuLevel}
          onAssignParentSku={onAssignParentSku}
          onManagePIAB={onManagePIAB}
          onCreateForecast={onCreateForecast}
          onManageImages={onManageImages}
        />
      </div>
    </TooltipProvider>
  )
}
